"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { Truck, Palette } from "lucide-react";
import Price from "@/components/ui/price";
import Badge from "@/components/ui/badge";
import Chip from "@/components/ui/chip";
import QuantityStepper from "@/components/ui/quantity-stepper";
import AddToCartButton from "./add-to-cart-button";
import type { Product, ProductVariant } from "@/lib/types";

type Props = {
  product: Product;
};

function findVariant(
  product: Product,
  selected: Record<string, string>,
): ProductVariant | null {
  return (
    product.variants.find((v) =>
      v.selectedOptions.every((o) => selected[o.name] === o.value),
    ) ?? null
  );
}

export default function ProductInfo({ product }: Props) {
  const initial = useMemo(() => {
    const map: Record<string, string> = {};
    for (const opt of product.options) map[opt.name] = opt.values[0];
    return map;
  }, [product]);

  const [selected, setSelected] = useState<Record<string, string>>(initial);
  const [qty, setQty] = useState(1);

  const variant = findVariant(product, selected) ?? product.variants[0];
  const unavailable = variant ? !variant.availableForSale : true;

  const isNew = product.tags.includes("new");
  const isBestseller = product.tags.includes("bestseller");
  const isPremium = product.tags.includes("premium");

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {isNew && <Badge tone="new">New</Badge>}
          {isBestseller && <Badge tone="sale">Bestseller</Badge>}
          {isPremium && <Badge tone="low-stock">Premium</Badge>}
          <span className="text-xs uppercase tracking-[0.14em] text-[color:var(--color-muted)]">
            {product.vendor}
          </span>
        </div>
        <h1
          className="text-3xl sm:text-4xl lg:text-[2.75rem] leading-tight"
          style={{ fontFamily: "var(--font-serif)", fontWeight: 500, letterSpacing: "-0.01em" }}
        >
          {product.title}
        </h1>
        <div className="mt-4 flex items-baseline gap-3">
          <Price
            amount={variant.price}
            compareAt={variant.compareAtPrice}
            size="lg"
          />
          <span className="text-sm text-[color:var(--color-muted)]">per m²</span>
        </div>
      </div>

      <p className="text-[15px] leading-relaxed text-[color:var(--color-text)]/90 max-w-[36rem]">
        {product.description}
      </p>

      {product.options.map((option) => (
        <div key={option.id} className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.14em] font-medium text-[color:var(--color-muted)]">
              {option.name}
            </div>
            <div className="text-sm font-medium">{selected[option.name]}</div>
          </div>
          <ul className="flex flex-wrap gap-2">
            {option.values.map((value) => (
              <li key={value}>
                <Chip
                  active={selected[option.name] === value}
                  onClick={() =>
                    setSelected((prev) => ({ ...prev, [option.name]: value }))
                  }
                >
                  {value}
                </Chip>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="flex flex-col gap-3">
        <div className="text-xs uppercase tracking-[0.14em] font-medium text-[color:var(--color-muted)]">
          Quantity
        </div>
        <div className="flex items-center gap-3">
          <QuantityStepper value={qty} onChange={setQty} />
          <span className="text-sm text-[color:var(--color-muted)]">
            packs · covers ~{qty * 1.44} m²
          </span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-2">
        <AddToCartButton disabled={unavailable} className="sm:flex-1" />
        <Link
          href="/contact"
          className="inline-flex h-12 items-center justify-center gap-2 rounded-[var(--radius-md)] border border-[color:var(--color-border-strong)] bg-[color:var(--color-surface)] px-6 text-sm font-medium hover:bg-[color:var(--color-surface-alt)]"
        >
          <Palette size={18} />
          Order a sample
        </Link>
      </div>

      <ul className="mt-4 grid grid-cols-2 gap-3 text-sm border-t border-[color:var(--color-border)] pt-4">
        <li className="flex items-start gap-2">
          <Truck size={16} className="mt-0.5 text-[color:var(--color-primary)]" />
          <div>
            <div className="font-medium">UK delivery</div>
            <div className="text-xs text-[color:var(--color-muted)]">3–5 working days</div>
          </div>
        </li>
        <li className="flex items-start gap-2">
          <Palette size={16} className="mt-0.5 text-[color:var(--color-primary)]" />
          <div>
            <div className="font-medium">Free samples</div>
            <div className="text-xs text-[color:var(--color-muted)]">Up to 4 per order</div>
          </div>
        </li>
      </ul>
    </div>
  );
}
